declare global {
  interface Window {
    afInitSocialShare?: (container?: HTMLElement | Document) => void;
  }
}

export function showToast(message: string, isError = false): void {
  if (typeof document === 'undefined') return;
  let toast = document.getElementById('af-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.id = 'af-toast';
    document.body.appendChild(toast);
  }
  toast.className = `fixed bottom-6 right-6 z-50 px-4 py-3 rounded-2xl font-mono text-xs font-semibold shadow-2xl flex items-center gap-2.5 transition-all duration-300 pointer-events-auto ${
    isError
      ? 'bg-[#D90429] text-white border border-red-400'
      : 'bg-[#0A0A0C] text-white dark:bg-white dark:text-[#0A0A0C] border border-slate-700 dark:border-slate-300'
  }`;
  toast.innerHTML = `<span class="material-symbols-outlined text-base">${isError ? 'error' : 'check_circle'}</span><span>${message}</span>`;
  toast.style.transform = 'translateY(0)';
  toast.style.opacity = '1';

  const customToast = toast as unknown as { _timeout?: ReturnType<typeof setTimeout> };
  if (customToast._timeout) {
    clearTimeout(customToast._timeout);
  }
  customToast._timeout = setTimeout(() => {
    if (toast) {
      toast.style.transform = 'translateY(16px)';
      toast.style.opacity = '0';
      toast.style.pointerEvents = 'none';
    }
  }, 2500);
}

export function fallbackCopy(text: string, cb?: () => void): void {
  if (typeof document === 'undefined') return;
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-9999px';
  textArea.style.top = '0';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    document.execCommand('copy');
    if (cb) cb();
  } catch {
    showToast('Failed to copy link', true);
  } finally {
    if (textArea.parentNode) {
      textArea.parentNode.removeChild(textArea);
    }
  }
}

export function copyLinkAction(btn: HTMLElement, url: string): void {
  function setSuccessVisual(): void {
    const copyIcon = btn.querySelector('.share-copy-icon');
    const checkIcon = btn.querySelector('.share-check-icon');
    const textEl = btn.querySelector('.share-copy-text');
    const origText = textEl?.textContent;

    if (copyIcon) copyIcon.classList.add('hidden');
    if (checkIcon) checkIcon.classList.remove('hidden');
    if (textEl) textEl.textContent = 'Copied!';

    showToast('Link copied to clipboard!');

    setTimeout(() => {
      if (copyIcon) copyIcon.classList.remove('hidden');
      if (checkIcon) checkIcon.classList.add('hidden');
      if (textEl && origText) textEl.textContent = origText;
    }, 2000);
  }

  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    navigator.clipboard.writeText(url).then(
      () => setSuccessVisual(),
      () => fallbackCopy(url, setSuccessVisual)
    );
  } else {
    fallbackCopy(url, setSuccessVisual);
  }
}

export function initSocialShare(container?: HTMLElement | Document): void {
  const scope = container || (typeof document !== 'undefined' ? document : null);
  if (!scope || typeof scope.addEventListener !== 'function') return;

  scope.addEventListener('click', function (e: Event): void {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const btn = target.closest<HTMLElement>(
      'button[data-share-platform], button[data-share-native], button[data-share-copy]'
    );
    if (!btn) return;

    const shareContainer = btn.closest<HTMLElement>('[data-social-share-container]');
    const rawUrl =
      btn.getAttribute('data-share-url') ||
      shareContainer?.getAttribute('data-share-url') ||
      (typeof window !== 'undefined' ? window.location.href : '');
    const title =
      btn.getAttribute('data-share-title') ||
      shareContainer?.getAttribute('data-share-title') ||
      (typeof document !== 'undefined' ? document.title : '');
    const desc =
      btn.getAttribute('data-share-desc') || shareContainer?.getAttribute('data-share-desc') || '';

    const url =
      rawUrl.startsWith('http://') || rawUrl.startsWith('https://')
        ? rawUrl
        : typeof window !== 'undefined' && window.location
          ? window.location.origin + rawUrl
          : rawUrl;

    // 1. Native Web Share API
    if (btn.hasAttribute('data-share-native')) {
      e.preventDefault();
      if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
        navigator
          .share({
            title: title,
            text: desc || title,
            url: url,
          })
          .catch(() => {
            // Ignore user cancellation
          });
      } else {
        copyLinkAction(btn, url);
      }
      return;
    }

    // 2. Clipboard Copy
    if (btn.hasAttribute('data-share-copy')) {
      e.preventDefault();
      copyLinkAction(btn, url);
      return;
    }

    // 3. Social Platform Share
    const platform = btn.getAttribute('data-share-platform');
    if (platform) {
      e.preventDefault();
      let shareUrl = '';

      switch (platform.toLowerCase()) {
        case 'linkedin':
          shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
          break;
        case 'twitter':
        case 'x':
          shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
          break;
        case 'facebook':
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
          break;
        case 'whatsapp':
          shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent((title ? title + ' ' : '') + url)}`;
          break;
        default:
          return;
      }

      if (typeof window !== 'undefined') {
        const width = 600;
        const height = 500;
        const screenX = typeof window.screenX !== 'undefined' ? window.screenX : 0;
        const screenY = typeof window.screenY !== 'undefined' ? window.screenY : 0;
        const outerWidth = typeof window.outerWidth !== 'undefined' ? window.outerWidth : 1024;
        const outerHeight = typeof window.outerHeight !== 'undefined' ? window.outerHeight : 768;
        const left = screenX + (outerWidth - width) / 2;
        const top = screenY + (outerHeight - height) / 2;
        window.open(
          shareUrl,
          'social-share-window',
          `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,resizable=yes`
        );
      }
    }
  });
}

if (typeof window !== 'undefined') {
  window.afInitSocialShare = initSocialShare;
}
